import React, { useRef } from "react";
import { cn } from "./cn";
import Badge from "./Badge";
import { Select } from "./Field";

/**
 * Bun underline tabs (household sections, page-level navigation).
 * tabs: [{ id, label, count?, countTone? }]. mobileSelect uses a dropdown below sm.
 */
export default function Tabs({ tabs, active, onChange, ariaLabel, id, className, mobileSelect = false }) {
  const tabRefs = useRef([]);
  const hasActiveTab = tabs.some((tab) => tab.id === active);

  const moveFocus = (event, index) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = index;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = tabs.length - 1;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
    const nextTab = tabs[nextIndex];
    if (!nextTab) return;
    tabRefs.current[nextIndex]?.focus();
    onChange(nextTab.id);
  };

  return (
    <>
    {mobileSelect && (
      <div className={cn("sm:hidden", className)}>
        <Select aria-label={ariaLabel || "Sections"} value={active} onChange={(event) => onChange(event.target.value)}>
          {tabs.map((tab) => <option key={tab.id} value={tab.id}>{tab.label}{tab.count != null ? ` (${tab.count})` : ''}</option>)}
        </Select>
      </div>
    )}
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn("flex gap-[22px] overflow-x-auto overflow-y-hidden border-b border-bun-line font-instrument", mobileSelect && "hidden sm:flex", className)}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            id={tab.tabId || (id ? `${id}-${tab.id}-tab` : undefined)}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls={tab.panelId}
            tabIndex={isActive || (!hasActiveTab && index === 0) ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => moveFocus(event, index)}
            className={cn(
              "inline-flex items-center gap-[6px] px-[2px] py-[9px] -mb-px text-bun-body cursor-pointer",
              "border-b-2 bg-transparent focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-bun-blue",
              isActive
                ? "font-semibold text-bun-text border-bun-blue"
                : "font-strong text-bun-stone border-transparent hover:text-bun-text"
            )}
          >
            {tab.label}
            {tab.count != null && (
              <Badge variant="count" tone={tab.countTone || "danger"}>
                {tab.count}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
    </>
  );
}
